import type {
  Format,
  InteractionContent,
  NewsletterOptIn,
  SubscriberAccess,
  ThemeOverrides,
} from '@quizee/core';
import type {
  ApiKey,
  AuditPage,
  AuditQuery,
  BillingInterval,
  BillingLink,
  BillingView,
  BrandConfig,
  BrandDetail,
  BrandSummary,
  CreatedApiKey,
  EmailList,
  EspAudience,
  EspConnection,
  ExportLink,
  Interaction,
  InteractionResults,
  InteractionSummary,
  Invite,
  InviteDetails,
  ListBackfillQueued,
  ListSyncResult,
  Member,
  MemberRole,
  OrgSettingsView,
  PublishResult,
  Session,
  SignupResult,
  SubscriberFilter,
  SubscriberPage,
  SubscriptionSummary,
  TeamList,
  UsageView,
  Webhook,
  WebhookDelivery,
  WebhookDeliveryStatus,
  WebhookRotated,
  WebhookSaved,
} from './types';

/**
 * The only place in the app that talks to the API.
 *
 * Everything goes through request(): one base URL, cookies always sent (the session cookie is
 * HttpOnly and lives on a sibling subdomain), and any non-2xx turned into an ApiError carrying a
 * sentence an editor can read. Components catch ApiError, they never see fetch or a status code.
 */

const DEFAULT_BASE_URL = 'https://api.quizee.io';

function baseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const value = configured && configured.length > 0 ? configured : DEFAULT_BASE_URL;
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * A second word from the body, for the refusals that have one.
   *
   * Only `article_unreachable` carries one today: a closed set saying whether the site did not
   * answer, answered with something that is not a web page, or is not on the public internet.
   * Those need different sentences in the editor, which is why the API answers with a word to
   * switch on rather than a message to parse. Null everywhere else.
   */
  readonly reason: string | null;

  constructor(status: number, code: string, message: string, reason: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.reason = reason;
  }

  /** The session has gone. Callers send the user back to the sign in page. */
  get isUnauthorised(): boolean {
    return this.status === 401;
  }
}

/** Turned into an error message when the server gives us nothing useful to show. */
function fallbackMessage(status: number): string {
  if (status === 401) return 'Your session has ended. Please sign in again.';
  if (status === 403) return 'You do not have access to this.';
  if (status === 404) return 'We could not find that.';
  if (status === 409) return 'That has already been taken. Try something else.';
  if (status === 429) return 'Too many attempts. Wait a moment and try again.';
  if (status >= 500) return 'Something went wrong at our end. Please try again.';
  return 'That did not work. Check what you have entered and try again.';
}

type UnauthorisedHandler = () => void;

let onUnauthorised: UnauthorisedHandler | null = null;

/**
 * The auth context registers here so a 401 from any request clears the signed in user, which is
 * what sends the reader of a stale tab back to the sign in page.
 */
export function setUnauthorisedHandler(handler: UnauthorisedHandler | null): void {
  onUnauthorised = handler;
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  /** Set for the session check, which treats a 401 as a normal answer rather than a failure. */
  readonly quiet?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers:
      options.body === undefined
        ? { accept: 'application/json' }
        : { accept: 'application/json', 'content-type': 'application/json' },
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);

  let response: Response;
  try {
    response = await fetch(`${baseUrl()}${path}`, init);
  } catch {
    throw new ApiError(0, 'network_error', 'We could not reach quizee. Check your connection.');
  }

  if (!response.ok) {
    let code = 'request_failed';
    let message = fallbackMessage(response.status);
    let reason: string | null = null;
    try {
      const body: unknown = await response.json();
      if (body !== null && typeof body === 'object') {
        const shape = body as { error?: unknown; message?: unknown; reason?: unknown };
        if (typeof shape.error === 'string' && shape.error.length > 0) code = shape.error;
        if (typeof shape.message === 'string' && shape.message.length > 0) message = shape.message;
        if (typeof shape.reason === 'string' && shape.reason.length > 0) reason = shape.reason;
      }
    } catch {
      // No JSON body. The fallback sentence is what the user sees.
    }
    if (response.status === 401 && options.quiet !== true) onUnauthorised?.();
    throw new ApiError(response.status, code, message, reason);
  }

  if (response.status === 204) return undefined as T;

  // A 202 with nothing in it is a normal answer for the send an email routes.
  const text = await response.text();
  if (text.trim().length === 0) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(response.status, 'bad_response', 'We got an unexpected reply from quizee.');
  }
}

/**
 * A change to the two inheritable settings, where three states have to survive the wire.
 *
 * Leaving a field out means "leave it as it is". Sending null means "stop overriding this and go
 * back to inheriting". Sending a value sets one, and for the consent notice an empty string is a
 * value: it means "show readers no notice". Undefined is the only one that is dropped, which is
 * what `configBody` does below, so a caller can build a patch without assembling JSON by hand.
 */
export interface ConfigPatch {
  readonly theme?: ThemeOverrides | null;
  readonly consent_text?: string | null;
  /**
   * The newsletter wording, on exactly the same three state terms as the notice above.
   *
   * Separate from `consent_text` all the way down, because they are separate statements and are
   * resolved per field: a brand that writes its own newsletter wording keeps the publisher's
   * answer notice, and the other way round.
   */
  readonly newsletter_consent_text?: string | null;
}

function configBody(patch: ConfigPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.theme !== undefined) body['theme'] = patch.theme;
  if (patch.consent_text !== undefined) body['consent_text'] = patch.consent_text;
  if (patch.newsletter_consent_text !== undefined) {
    body['newsletter_consent_text'] = patch.newsletter_consent_text;
  }
  return body;
}

/**
 * A change to the publisher's defaults, on the same three state terms as a brand.
 *
 * `PUT /v1/orgs/:orgId/settings` is a merge over the fields the body actually names, so a field
 * left out is left alone rather than cleared. That is what lets a screen send only the section
 * somebody edited: two people changing different sections of the same publisher no longer
 * overwrite each other with values neither of them looked at. Null still means "nothing set at
 * this level", and for the consent notice an empty string is a value meaning "show no notice".
 */
export interface OrgSettingsPatch extends ConfigPatch {
  readonly newsletter_opt_in?: NewsletterOptIn | null;
  /**
   * Who may see and export the subscribers. Owners only, or owners and members.
   *
   * No null, unlike the fields beside it: there is no layer under this one for a clearing to let
   * through, and an unset record already means owners only.
   */
  readonly subscriber_access?: SubscriberAccess;
}

/**
 * A query string from what a screen has actually set.
 *
 * Anything undefined or empty is left out rather than sent as a blank, because the API's filters
 * are strict and a blank is not the same request as no filter at all.
 */
function query(params: Readonly<Record<string, string | undefined>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** The same filters as an object, for the export, which takes them in a body rather than a url. */
function filterBody(filter: SubscriberFilter): Record<string, string> {
  const body: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== '') body[key] = value;
  }
  return body;
}

export const api = {
  /**
   * A person is two fields, and a last name is optional.
   *
   * The API stores `first_name` and `last_name` and joins them itself when a screen needs one
   * name, so nothing here sends a joined `name`: there is no alias for it and a body carrying one
   * is refused. Somebody with a single name sends an empty last name rather than being made to
   * invent one.
   */
  signup(input: {
    orgName: string;
    brandName: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }): Promise<SignupResult> {
    return request<SignupResult>('/v1/auth/signup', {
      method: 'POST',
      body: {
        org_name: input.orgName,
        brand_name: input.brandName,
        first_name: input.firstName,
        last_name: input.lastName,
        email: input.email,
        password: input.password,
      },
    });
  },

  login(email: string, password: string): Promise<Session> {
    return request<Session>('/v1/auth/login', { method: 'POST', body: { email, password } });
  },

  logout(): Promise<void> {
    return request<void>('/v1/auth/logout', { method: 'POST' });
  },

  me(): Promise<Session> {
    return request<Session>('/v1/auth/me', { quiet: true });
  },

  requestEmailVerification(): Promise<void> {
    return request<void>('/v1/auth/verify-email/request', { method: 'POST' });
  },

  confirmEmailVerification(token: string): Promise<{ verified: boolean }> {
    return request<{ verified: boolean }>('/v1/auth/verify-email/confirm', {
      method: 'POST',
      body: { token },
    });
  },

  requestPasswordReset(email: string): Promise<void> {
    return request<void>('/v1/auth/password-reset', { method: 'POST', body: { email } });
  },

  confirmPasswordReset(token: string, password: string): Promise<void> {
    return request<void>('/v1/auth/password-reset/confirm', {
      method: 'POST',
      body: { token, password },
    });
  },

  members(orgId: string): Promise<TeamList> {
    return request<TeamList>(`/v1/orgs/${encodeURIComponent(orgId)}/members`);
  },

  /**
   * Change what somebody can do here. Owners only.
   *
   * Refused with `last_owner` when it would leave the publisher with nobody who can manage it,
   * which is a rule rather than a failure: see `isLastOwner` below, and the team screen, which
   * does not offer the change in the first place.
   */
  updateMember(orgId: string, userId: string, role: MemberRole): Promise<{ member: Member }> {
    return request<{ member: Member }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
      { method: 'PUT', body: { role } },
    );
  },

  /**
   * Take somebody off this publisher. Owners only, and refused with `last_owner` on the last one.
   *
   * Their sessions end with the membership, so they are signed out wherever they are signed in.
   * The screen says that before anybody presses it.
   */
  removeMember(orgId: string, userId: string): Promise<void> {
    return request<void>(
      `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    );
  },

  createInvite(orgId: string, email: string, role: string): Promise<{ invite: Invite }> {
    return request<{ invite: Invite }>(`/v1/orgs/${encodeURIComponent(orgId)}/invites`, {
      method: 'POST',
      body: { email, role },
    });
  },

  cancelInvite(orgId: string, inviteId: string): Promise<void> {
    return request<void>(
      `/v1/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    );
  },

  invite(token: string): Promise<InviteDetails> {
    return request<InviteDetails>(`/v1/invites/${encodeURIComponent(token)}`);
  },

  /** Pass the name and password for somebody new, or null when they are already signed in. */
  acceptInvite(
    token: string,
    credentials: { firstName: string; lastName: string; password: string } | null,
  ): Promise<{ org_id: string }> {
    return request<{ org_id: string }>(`/v1/invites/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body:
        credentials === null
          ? {}
          : {
              first_name: credentials.firstName,
              last_name: credentials.lastName,
              password: credentials.password,
            },
    });
  },

  /** The defaults every brand under this publisher starts from. */
  orgSettings(orgId: string): Promise<OrgSettingsView> {
    return request<OrgSettingsView>(`/v1/orgs/${encodeURIComponent(orgId)}/settings`);
  },

  /**
   * Change the publisher's defaults, naming only what is being changed.
   *
   * Undefined is dropped rather than sent, so a body carries the fields one section owns and
   * nothing else. A patch naming nothing is a no op at the API, which returns the settings as they
   * stand, so an empty one is safe rather than destructive.
   */
  saveOrgSettings(orgId: string, patch: OrgSettingsPatch): Promise<OrgSettingsView> {
    const body: Record<string, unknown> = configBody(patch);
    if (patch.newsletter_opt_in !== undefined) {
      body['newsletter_opt_in'] = patch.newsletter_opt_in;
    }
    if (patch.subscriber_access !== undefined) {
      body['subscriber_access'] = patch.subscriber_access;
    }
    return request<OrgSettingsView>(`/v1/orgs/${encodeURIComponent(orgId)}/settings`, {
      method: 'PUT',
      body,
    });
  },

  brands(orgId: string): Promise<{ brands: BrandSummary[] }> {
    return request<{ brands: BrandSummary[] }>(`/v1/orgs/${encodeURIComponent(orgId)}/brands`);
  },

  brand(orgId: string, brandId: string): Promise<BrandDetail> {
    return request<BrandDetail>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}`,
    );
  },

  createBrand(
    orgId: string,
    input: { name: string; domains: readonly string[] } & ConfigPatch,
  ): Promise<{ brand: BrandConfig }> {
    return request<{ brand: BrandConfig }>(`/v1/orgs/${encodeURIComponent(orgId)}/brands`, {
      method: 'POST',
      body: { name: input.name, domains: input.domains, ...configBody(input) },
    });
  },

  updateBrand(
    orgId: string,
    brandId: string,
    patch: {
      name?: string;
      domains?: readonly string[];
      /** Null clears the brand's own choice and puts it back to inheriting the default. */
      newsletter_opt_in?: NewsletterOptIn | null;
    } & ConfigPatch,
  ): Promise<{ brand: BrandConfig }> {
    const body: Record<string, unknown> = configBody(patch);
    if (patch.name !== undefined) body['name'] = patch.name;
    if (patch.domains !== undefined) body['domains'] = patch.domains;
    if (patch.newsletter_opt_in !== undefined) {
      body['newsletter_opt_in'] = patch.newsletter_opt_in;
    }
    return request<{ brand: BrandConfig }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}`,
      { method: 'PUT', body },
    );
  },

  deleteBrand(orgId: string, brandId: string): Promise<void> {
    return request<void>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}`,
      { method: 'DELETE' },
    );
  },

  /**
   * Where this month has got to. Any member can read it, not only an owner.
   *
   * There is nothing to write here on purpose: a plan is a commercial agreement an operator
   * records, so the screen this feeds is something to read and never something to act on.
   */
  usage(orgId: string): Promise<UsageView> {
    return request<UsageView>(`/v1/orgs/${encodeURIComponent(orgId)}/usage`);
  },

  /**
   * What the publisher is paying and what can be changed from here. Any member can read it.
   *
   * The reply carries `configured` and `can_manage`, and a screen has to read both before drawing
   * anything to press: there is no Stripe account behind this yet, so `configured: false` is the
   * ordinary answer rather than a fault.
   */
  billing(orgId: string): Promise<BillingView> {
    return request<BillingView>(`/v1/orgs/${encodeURIComponent(orgId)}/billing`);
  },

  /**
   * Start paying for a plan. Owners only.
   *
   * The plan and the interval are named, never a price: what those two mean in money is settled on
   * the server against the published table, so nothing the browser sends can decide what anybody
   * is charged. What comes back is a Stripe hosted address, and the card is typed there and never
   * here.
   */
  startCheckout(
    orgId: string,
    input: { plan: string; interval: BillingInterval },
  ): Promise<BillingLink> {
    return request<BillingLink>(`/v1/orgs/${encodeURIComponent(orgId)}/billing/checkout`, {
      method: 'POST',
      body: { plan: input.plan, interval: input.interval },
    });
  },

  /**
   * A way into Stripe's own portal, where the card, the invoices and cancelling live. Owners only.
   *
   * Everything to do with a card happens on the other side of this link. Nothing in this app ever
   * sees a card number, asks for one or shows one back.
   */
  billingPortal(orgId: string): Promise<BillingLink> {
    return request<BillingLink>(`/v1/orgs/${encodeURIComponent(orgId)}/billing/portal`, {
      method: 'POST',
    });
  },

  interactions(orgId: string): Promise<{ interactions: InteractionSummary[] }> {
    return request<{ interactions: InteractionSummary[] }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions`,
    );
  },

  createInteraction(
    orgId: string,
    brandId: string,
    content: InteractionContent,
  ): Promise<{ interaction: Interaction }> {
    return request<{ interaction: Interaction }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions`,
      { method: 'POST', body: { brand_id: brandId, content } },
    );
  },

  /**
   * Ask a model for a first draft.
   *
   * It comes back as an ordinary interaction, saved as a draft with a provenance flag on it, which
   * is why there is nothing here to hold a generated document in the browser: an AI drafted
   * interaction is the same object as a human one from the moment it exists.
   *
   * Two ways in and they compose. A brief on its own is what drafting has always been, an article
   * address on its own says "make one of these out of this piece", and both together means the
   * article is the material and the brief is the steer. The API refuses a body carrying neither,
   * which is the only one it cannot answer, so a caller sends whichever it has.
   *
   * Which addresses may be read is decided by the API against the brand's own domains and is not
   * checked again here. It is the whole security argument for having a fetcher at all (D38), so it
   * lives in one place, and a second copy in the browser could only ever drift from it.
   */
  draftInteraction(
    orgId: string,
    input: { prompt?: string; articleUrl?: string; format: Format; brandId: string },
  ): Promise<{ interaction: Interaction }> {
    return request<{ interaction: Interaction }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions/draft`,
      {
        method: 'POST',
        body: {
          format: input.format,
          brand_id: input.brandId,
          ...(input.prompt === undefined ? {} : { prompt: input.prompt }),
          ...(input.articleUrl === undefined ? {} : { article_url: input.articleUrl }),
        },
      },
    );
  },

  /** Record that a person has read an AI drafted interaction and stands behind it. */
  approveInteraction(orgId: string, interactionId: string): Promise<{ interaction: Interaction }> {
    return request<{ interaction: Interaction }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions/${encodeURIComponent(interactionId)}/approve`,
      { method: 'POST' },
    );
  },

  interaction(orgId: string, interactionId: string): Promise<{ interaction: Interaction }> {
    return request<{ interaction: Interaction }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions/${encodeURIComponent(interactionId)}`,
    );
  },

  saveInteraction(
    orgId: string,
    interactionId: string,
    content: InteractionContent,
  ): Promise<{ interaction: Interaction }> {
    return request<{ interaction: Interaction }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions/${encodeURIComponent(interactionId)}`,
      { method: 'PUT', body: { content } },
    );
  },

  publishInteraction(orgId: string, interactionId: string): Promise<PublishResult> {
    return request<PublishResult>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions/${encodeURIComponent(interactionId)}/publish`,
      { method: 'POST' },
    );
  },

  results(orgId: string, interactionId: string): Promise<InteractionResults> {
    return request<InteractionResults>(
      `/v1/orgs/${encodeURIComponent(orgId)}/interactions/${encodeURIComponent(interactionId)}/results`,
    );
  },

  /** Every list on one brand, in the order the server hands them back. */
  lists(orgId: string, brandId: string): Promise<{ lists: EmailList[] }> {
    return request<{ lists: EmailList[] }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists`,
    );
  },

  /** Make a new list on a brand. Its own address, so a signup can be pointed at it. */
  createList(
    orgId: string,
    brandId: string,
    input: { name: string; description?: string },
  ): Promise<{ list: EmailList }> {
    return request<{ list: EmailList }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists`,
      {
        method: 'POST',
        body: {
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
        },
      },
    );
  },

  /**
   * Rename a list, or change its description. Naming neither leaves both as they are.
   *
   * A description of `null` clears it, which is a different request from leaving the field out.
   */
  updateList(
    orgId: string,
    brandId: string,
    listId: string,
    patch: { name?: string; description?: string | null },
  ): Promise<{ list: EmailList }> {
    const body: Record<string, unknown> = {};
    if (patch.name !== undefined) body['name'] = patch.name;
    if (patch.description !== undefined) body['description'] = patch.description;
    return request<{ list: EmailList }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists/${encodeURIComponent(listId)}`,
      { method: 'PUT', body },
    );
  },

  /**
   * Hide a list from the signup picker and stop new members joining it. Never a delete: the
   * consent records already on it are a publisher's evidence for having emailed somebody, and
   * everybody already on the list stays readable and exportable.
   *
   * Refused with a 409 for the default list, which is why a screen must never offer this action
   * for one: the refusal is a rule the server enforces, not a race worth designing a message for.
   */
  archiveList(orgId: string, brandId: string, listId: string): Promise<{ list: EmailList }> {
    return request<{ list: EmailList }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists/${encodeURIComponent(listId)}/archive`,
      { method: 'POST' },
    );
  },

  /**
   * The Mailchimp connection for this publisher, or null when nothing has been connected.
   *
   * Any member can read it: it says whether Mailchimp is connected and what it is doing, and none
   * of that is the key itself.
   */
  espConnection(orgId: string): Promise<{ connection: EspConnection | null }> {
    return request<{ connection: EspConnection | null }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/integrations/esp`,
    );
  },

  /**
   * Paste a Mailchimp key in, or replace one that is already there. Owners only.
   *
   * There is no way to change a key without sending the whole of it again: it is a credential
   * rather than an address beside one, so there is nothing narrower to send. Refused with
   * `invalid_credentials` when Mailchimp itself turns the key down.
   */
  connectEsp(orgId: string, apiKey: string): Promise<{ connection: EspConnection }> {
    return request<{ connection: EspConnection }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/integrations/esp`,
      { method: 'PUT', body: { provider: 'mailchimp', api_key: apiKey } },
    );
  },

  /** Disconnect Mailchimp. Every list stops syncing; nothing already sent there is removed. */
  disconnectEsp(orgId: string): Promise<void> {
    return request<void>(`/v1/orgs/${encodeURIComponent(orgId)}/integrations/esp`, {
      method: 'DELETE',
    });
  },

  /** Every audience Mailchimp will show this key, so a list can be pointed at one. */
  espAudiences(orgId: string): Promise<{ audiences: readonly EspAudience[] }> {
    return request<{ audiences: readonly EspAudience[] }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/integrations/esp/audiences`,
    );
  },

  /**
   * Point one list at one Mailchimp audience, replacing whatever it was pointed at before.
   *
   * The mapping is made either way, even when `double_optin_warning` comes back non-null: that
   * is Mailchimp's own setting checked fresh at this moment, not a refusal, because holding a
   * mapping hostage to a setting this product does not own would leave a publisher with nothing
   * while they go and look.
   */
  syncList(
    orgId: string,
    brandId: string,
    listId: string,
    audienceId: string,
  ): Promise<ListSyncResult> {
    return request<ListSyncResult>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists/${encodeURIComponent(listId)}/sync`,
      { method: 'PUT', body: { audience_id: audienceId } },
    );
  },

  /** Stop sending this list to Mailchimp. Nothing already there is removed, on either side. */
  unsyncList(orgId: string, brandId: string, listId: string): Promise<void> {
    return request<void>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists/${encodeURIComponent(listId)}/sync`,
      { method: 'DELETE' },
    );
  },

  /**
   * Send everybody already confirmed on this list to Mailchimp, not only new signups from now on.
   *
   * Answers once the push is queued, not once it has finished: a group's list can be a hundred
   * thousand people. Refused with `audience_double_optin` when the audience now double opt-ins,
   * checked fresh against Mailchimp at this moment because a remembered answer from when the list
   * was mapped could not catch a setting somebody changed since.
   */
  backfillListSync(orgId: string, brandId: string, listId: string): Promise<ListBackfillQueued> {
    return request<ListBackfillQueued>(
      `/v1/orgs/${encodeURIComponent(orgId)}/brands/${encodeURIComponent(brandId)}/lists/${encodeURIComponent(listId)}/sync/backfill`,
      { method: 'POST' },
    );
  },

  /** One page of newsletter signups, oldest first, under whichever filters are set. */
  subscribers(
    orgId: string,
    filter: SubscriberFilter & { cursor?: string; limit?: number } = {},
  ): Promise<SubscriberPage> {
    const { cursor, limit, ...rest } = filter;
    return request<SubscriberPage>(
      `/v1/orgs/${encodeURIComponent(orgId)}/subscriptions${query({
        ...filterBody(rest),
        ...(cursor === undefined ? {} : { cursor }),
        ...(limit === undefined ? {} : { limit: String(limit) }),
      })}`,
    );
  },

  /**
   * The counts for the same filters.
   *
   * Read the `truncated` flag before showing any of these. Past the cap they are a floor rather
   * than a total, and a floor presented as a total is a number a publisher would plan against.
   */
  subscriptionSummary(orgId: string, filter: SubscriberFilter = {}): Promise<SubscriptionSummary> {
    return request<SubscriptionSummary>(
      `/v1/orgs/${encodeURIComponent(orgId)}/subscriptions/summary${query(filterBody(filter))}`,
    );
  },

  /** Ask for a CSV. What comes back is a link that works once and for fifteen minutes. */
  exportSubscribers(orgId: string, filter: SubscriberFilter = {}): Promise<ExportLink> {
    return request<ExportLink>(`/v1/orgs/${encodeURIComponent(orgId)}/subscriptions/export`, {
      method: 'POST',
      body: filterBody(filter),
    });
  },

  /**
   * Where this publisher posts its signups.
   *
   * One webhook per org and no brand scoped version: a brand shares the org's data boundary, so
   * one url and one secret cover every masthead, and each delivery carries the brand it came from.
   * Any member can read it, because nothing here is the secret.
   */
  orgWebhook(orgId: string): Promise<{ webhook: Webhook | null }> {
    return request<{ webhook: Webhook | null }>(`/v1/orgs/${encodeURIComponent(orgId)}/webhook`);
  },

  /**
   * Set where the publisher posts its signups. Owners only.
   *
   * The secret comes back only when this call is what created the webhook. Changing the url of one
   * that already exists keeps the secret, and does not repeat it.
   */
  saveOrgWebhook(orgId: string, input: { url: string; active?: boolean }): Promise<WebhookSaved> {
    return request<WebhookSaved>(`/v1/orgs/${encodeURIComponent(orgId)}/webhook`, {
      method: 'PUT',
      body: { url: input.url, ...(input.active === undefined ? {} : { active: input.active }) },
    });
  },

  /** A hard cut: the new secret is printed once and the old one stops verifying immediately. */
  rotateOrgWebhookSecret(orgId: string): Promise<WebhookRotated> {
    return request<WebhookRotated>(`/v1/orgs/${encodeURIComponent(orgId)}/webhook/rotate`, {
      method: 'POST',
    });
  },

  deleteOrgWebhook(orgId: string): Promise<void> {
    return request<void>(`/v1/orgs/${encodeURIComponent(orgId)}/webhook`, { method: 'DELETE' });
  },

  webhookDeliveries(
    orgId: string,
    status?: WebhookDeliveryStatus,
  ): Promise<{ deliveries: readonly WebhookDelivery[] }> {
    return request<{ deliveries: readonly WebhookDelivery[] }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/webhook/deliveries${query({ status })}`,
    );
  },

  apiKeys(orgId: string): Promise<{ api_keys: readonly ApiKey[] }> {
    return request<{ api_keys: readonly ApiKey[] }>(
      `/v1/orgs/${encodeURIComponent(orgId)}/api-keys`,
    );
  },

  /** The only reply that will ever carry the key itself. Only its hash is stored. */
  createApiKey(orgId: string, name: string): Promise<CreatedApiKey> {
    return request<CreatedApiKey>(`/v1/orgs/${encodeURIComponent(orgId)}/api-keys`, {
      method: 'POST',
      body: { name },
    });
  },

  revokeApiKey(orgId: string, keyId: string): Promise<void> {
    return request<void>(
      `/v1/orgs/${encodeURIComponent(orgId)}/api-keys/${encodeURIComponent(keyId)}`,
      { method: 'DELETE' },
    );
  },

  /**
   * Who changed this publisher's settings, and when. Owners only, and paged with a cursor.
   *
   * The cursor is the server's own sort key, so it is passed back exactly as it arrived and never
   * built here. There is no page number to ask for: pages only go forwards, one after the last
   * record already shown.
   */
  /**
   * One page of the settings audit trail, newest first, optionally inside a date range.
   *
   * The range is instants rather than days, and a cursor only means anything inside the range it
   * was issued for, so a caller changing the range starts the list again rather than carrying one
   * across.
   */
  auditTrail(orgId: string, options: AuditQuery = {}): Promise<AuditPage> {
    return request<AuditPage>(
      `/v1/orgs/${encodeURIComponent(orgId)}/audit${query({
        from: options.from,
        to: options.to,
        cursor: options.cursor,
      })}`,
    );
  },
};

/**
 * Whether a refusal is this publisher's own rule about who may see their subscribers.
 *
 * The API answers with its own code rather than a bare 403 so that a screen can tell "you are not
 * allowed to see these" apart from every other way a request can fail, and say the first one in
 * words. A member who is refused the list must never be shown an empty table: an empty table says
 * nobody has signed up, which is a different thing and usually a lie.
 */
export function isSubscriberDataRestricted(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'subscriber_data_restricted';
}

/**
 * Whether a refusal is the rule that a publisher must always have an owner.
 *
 * Told apart from every other refusal because it is not a failure: it means the change would have
 * left nobody able to manage the publisher, and the answer is to make somebody else an owner
 * first, not to try again. The team screen keeps the action out of reach when it can see this
 * coming, so reaching this code at all means two people were changing the team at once.
 */
export function isLastOwner(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'last_owner';
}

/**
 * Whether a refusal is Mailchimp turning down the key itself, rather than anything else failing.
 *
 * Told apart from every other refusal because the sentence it deserves is not the server's
 * message but a fixed one naming where in Mailchimp to find the right key.
 */
export function isInvalidEspCredentials(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'invalid_credentials';
}

/**
 * Whether a backfill was refused because the audience it would push into still double opt-ins.
 *
 * Checked at the API against Mailchimp at the moment the backfill is requested, not at the moment
 * the list was mapped, because a publisher can turn double opt-in on or off in Mailchimp at any
 * time and a stale answer would be worse than none. The message on this refusal is already
 * written in an editor's words by the API, the same sentence the connection warning uses, so the
 * screen shows it as it stands rather than writing a second version of the same explanation.
 */
export function isAudienceDoubleOptIn(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'audience_double_optin';
}

/** One place to turn any thrown value into a sentence, so no stack trace reaches the screen. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong. Please try again.';
}
